// Distribution of a parameter's readings on bins shared by several devices, as the parameters
// summary of custom air quality reports draws it. Ported from the analytics service
// (app/utils/aq_distribution_helpers.py and the severities of app/utils/alert_levels.py), so
// the histogram widget bins readings the same way the report does.

export const TARGET_BINS = 15;
export const MAX_BINS = 20;
const NICE_STEPS = [1, 2, 2.5, 5];
const EPSILON = 1e-9;
const MINUTE_MS = 60_000;

export type HistogramLevelExpression = 'gt' | 'gte' | 'lt' | 'lte';
export type HistogramLevel = { expr: HistogramLevelExpression; value: number };

// Status of a value under the alert levels: 0 passed, 1 warning, 2 failed
export type Severity = 0 | 1 | 2;

// Underflow ("< x") bins have no lower bound, overflow ("≥ x") bins no upper bound
export type HistogramBin = { label: string; lower: number | null; upper: number | null; severity: Severity };

// A level on the x axis, at `position` in bin edge units, in the severity of its worse side
export type LevelLine = { value: number; position: number; severity: Severity };

export type BinLayout = { width: number; origin: number; end: number; bins: HistogramBin[]; levelLines: LevelLine[] };

export type ValueStats = { min: number | null; avg: number | null; max: number | null; p50: number | null; p95: number | null; p98: number | null };

export type LevelShare = { severity: Severity; percent: number };

export type Distribution = ValueStats & {
  readings: number;
  // Percent of the readings in each bin, in the order of the bins
  percents: number[];
  // Percent of the readings in each alert severity, only with alert levels
  levelShares: LevelShare[];
};

export type Coverage = { percent: number; missingMinutes: number };

export type DeviceReadings = { id: string; readings: Array<[number, number]> };

export type DeviceDistribution = Distribution & { id: string; coverage: number | null; missingMinutes: number | null };

export type HistogramSummary = {
  hasLevels: boolean;
  width: number;
  widthLabel: string;
  bins: HistogramBin[];
  edgeLabels: string[];
  levelLines: LevelLine[];
  // Shared maximum of the y axes (percent)
  yMax: number;
  devices: DeviceDistribution[];
};

const round = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

// Numeric value of a [timestamp, value] measurement; aggregated values are {avg, ...} and
// metric values may come wrapped in an array
export function measurementValue(measurement: unknown): number | null {
  if (!Array.isArray(measurement) || measurement.length < 2) return null;
  let value = measurement[1];
  if (Array.isArray(value)) [value] = value;
  if (value !== null && typeof value === 'object') value = (value as { avg?: unknown }).avg;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

// Percentile q (0-1) of ascending values, linearly interpolated between closest ranks
export function percentile(sorted: number[], q: number): number | null {
  if (sorted.length === 0) return null;
  const position = q * (sorted.length - 1);
  const lower = Math.floor(position);
  const upper = Math.min(lower + 1, sorted.length - 1);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

export function valueStats(sorted: number[]): ValueStats {
  const hasValues = sorted.length > 0;
  return {
    min: hasValues ? sorted[0] : null,
    avg: hasValues ? sorted.reduce((sum, value) => sum + value, 0) / sorted.length : null,
    max: hasValues ? sorted[sorted.length - 1] : null,
    p50: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    p98: percentile(sorted, 0.98)
  };
}

const isUpward = (expr: HistogramLevelExpression) => expr === 'gt' || expr === 'gte';

function breaches(value: number, { expr, value: level }: HistogramLevel) {
  switch (expr) {
    case 'gt':
      return value > level;
    case 'gte':
      return value >= level;
    case 'lt':
      return value < level;
    default:
      return value <= level;
  }
}

// Severity of a value as the report colors it: a single level, or two levels in opposite
// directions, are failed once breached; of two levels in the same direction the first one
// breached is a warning and the more severe one (higher for > / ≥, lower for < / ≤) failed
export function valueSeverity(value: number, allLevels: HistogramLevel[]): Severity {
  const levels = allLevels.slice(0, 2);
  const breached = levels.filter((level) => breaches(value, level));
  if (breached.length === 0) return 0;
  if (levels.length === 1 || isUpward(levels[0].expr) !== isUpward(levels[1].expr)) return 2;
  const [first, second] = levels;
  const higher = second.value > first.value ? second : first;
  const lower = higher === first ? second : first;
  const severe = isUpward(first.expr) ? higher : lower;
  return breached.includes(severe) ? 2 : 1;
}

// A level an alert severity comes from: its position in the form (0-based) and its name, if any
export type SeverityLevel = { index: number; name: string | null };

// The levels each alert severity comes from, as the report labels its level shares: of two levels
// in the same direction the more severe one fails and the other warns; a single level, or levels
// in opposite directions, all fail
export function severityLevels(allLevels: Array<HistogramLevel & { name?: string | null }>): Partial<Record<Severity, SeverityLevel[]>> {
  const levels = allLevels.slice(0, 2).map(({ expr, value, name }, index) => ({ expr, value, index, name: (name || '').trim() || null }));
  const describe = (list: typeof levels) => list.map(({ index, name }) => ({ index, name }));
  if (levels.length === 0) return {};
  if (levels.length === 1 || isUpward(levels[0].expr) !== isUpward(levels[1].expr)) return { 2: describe(levels) };
  const [first, second] = levels;
  const higher = second.value > first.value ? second : first;
  const severe = isUpward(first.expr) ? higher : higher === first ? second : first;
  const other = severe === first ? second : first;
  return { 1: describe([other]), 2: describe([severe]) };
}

// Finite level values, ascending: the boundaries of the severity bands
export function levelBoundaries(levels: HistogramLevel[]): number[] {
  return Array.from(new Set(levels.slice(0, 2).map(({ value }) => value)))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
}

// Alert severities (warning, failed) the levels give some values, ascending
export function alertSeverities(levels: HistogramLevel[]): Severity[] {
  const points = levelBoundaries(levels);
  if (points.length === 0) return [];
  const margin = Math.max(1, points[points.length - 1] - points[0]);
  const samples = [
    points[0] - margin,
    ...points.flatMap((point, index) => [point, index + 1 < points.length ? (point + points[index + 1]) / 2 : point + margin])
  ];
  return Array.from(new Set(samples.map((sample) => valueSeverity(sample, levels))))
    .filter((severity) => severity > 0)
    .sort();
}

// Bin widths 1, 2, 2.5, 5 x 10^n in ascending order, from the first one >= start
function* niceWidths(start: number) {
  let exponent = start > 0 ? Math.floor(Math.log10(start)) : 0;
  while (true) {
    for (const step of NICE_STEPS) {
      const width = step * 10 ** exponent;
      if (width >= start - EPSILON * Math.abs(start)) yield width;
    }
    exponent += 1;
  }
}

function isMultiple(value: number, width: number) {
  const ratio = value / width;
  return Math.abs(ratio - Math.round(ratio)) < 1e-6;
}

// Origin, width and count of the core bins covering low-high and every boundary. The width is
// a nice number giving about TARGET_BINS bins (at most MAX_BINS); with alert levels, the first
// width from there up that puts every level on a bin edge, keeping one bin beyond the outermost
// levels. If only a much coarser width would, bins straddle a level.
function binLayout(rawLow: number, rawHigh: number, boundaries: number[]): [number, number, number] {
  let low = rawLow;
  let high = rawHigh;
  if (boundaries.length > 0) {
    low = Math.min(low, boundaries[0]);
    high = Math.max(high, boundaries[boundaries.length - 1]);
  }
  let span = high - low;
  if (span <= 0) span = Math.abs(high) || 1;

  const layout = (width: number): [number, number, number] => {
    let origin = Math.floor(low / width + EPSILON) * width;
    let end = Math.ceil(high / width - EPSILON) * width;
    if (boundaries.length > 0) {
      // Keep a bin on the far side of the outermost levels, so their bands show
      if (origin > boundaries[0] - EPSILON) origin -= width;
      if (end < boundaries[boundaries.length - 1] + EPSILON) end += width;
    }
    return [origin, width, Math.max(1, Math.round((end - origin) / width))];
  };

  const widths = niceWidths(span / TARGET_BINS);
  let fallback: [number, number, number] | null = null;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const candidate = layout(widths.next().value as number);
    const [origin, width, count] = candidate;
    if (count <= MAX_BINS) {
      if (!fallback) fallback = candidate;
      if (boundaries.every((bound) => isMultiple(bound - origin, width))) return candidate;
      // Do not coarsen past half the target bin count just to align the levels
      if (count < TARGET_BINS / 2) break;
    }
  }
  return fallback ?? layout(niceWidths(span / MAX_BINS).next().value as number);
}

// Number with as many decimals as the bin width needs
export function formatNumber(value: number, width: number): string {
  let decimals = 0;
  while (decimals < 6 && !isMultiple(width, 10 ** -decimals)) decimals += 1;
  const text = value.toFixed(decimals);
  return /^-0(\.0+)?$/.test(text) ? '0' : text;
}

// Bins shared by all devices of a parameter, and its level lines. Core bins cover the 1st-99th
// percentile of all readings plus the alert levels; readings beyond them fall into an underflow
// ("< x") or overflow ("≥ x") bin. Each bin takes the severity at its middle.
export function buildBins(values: number[], levels: HistogramLevel[]): BinLayout {
  const ordered = [...values].sort((a, b) => a - b);
  const boundaries = levelBoundaries(levels);
  const hasLevels = levels.length > 0;
  const low = ordered.length > 0 ? (percentile(ordered, 0.01) as number) : (boundaries[0] ?? 0);
  const high = ordered.length > 0 ? (percentile(ordered, 0.99) as number) : (boundaries[boundaries.length - 1] ?? 1);
  const [origin, width, count] = binLayout(low, high, boundaries);
  const end = origin + count * width;
  const severityAt = (value: number): Severity => (hasLevels ? valueSeverity(value, levels) : 0);

  const bins: HistogramBin[] = [];
  const underflow = ordered.length > 0 && ordered[0] < origin - EPSILON;
  if (underflow) {
    bins.push({ label: `< ${formatNumber(origin, width)}`, lower: null, upper: origin, severity: severityAt(origin - width / 2) });
  }
  for (let index = 0; index < count; index += 1) {
    const lower = origin + index * width;
    const upper = origin + (index + 1) * width;
    bins.push({ label: `${formatNumber(lower, width)}–${formatNumber(upper, width)}`, lower, upper, severity: severityAt((lower + upper) / 2) });
  }
  if (ordered.length > 0 && ordered[ordered.length - 1] >= end - EPSILON) {
    bins.push({ label: `≥ ${formatNumber(end, width)}`, lower: end, upper: null, severity: severityAt(end + width / 2) });
  }

  const offset = underflow ? 1 : 0;
  const delta = width / 1000;
  const levelLines = boundaries
    .filter((bound) => origin - EPSILON <= bound && bound <= end + EPSILON)
    .map((bound) => ({
      value: bound,
      position: (bound - origin) / width + offset,
      severity: Math.max(severityAt(bound - delta), severityAt(bound + delta)) as Severity
    }));
  return { width, origin, end, bins, levelLines };
}

// Labels of the bin edges, left to right (one more than the bins); the open ends of the
// underflow and overflow bins are blank
export function edgeLabels(bins: HistogramBin[], width: number): string[] {
  const lowers = bins.map(({ lower }) => (lower === null ? '' : formatNumber(lower, width)));
  const last = bins.length > 0 ? bins[bins.length - 1].upper : null;
  return [...lowers, last === null ? '' : formatNumber(last, width)];
}

// Index of the bin a value falls into
export function binIndex(value: number, { bins, origin, width }: Pick<BinLayout, 'bins' | 'origin' | 'width'>): number {
  const offset = bins.length > 0 && bins[0].lower === null ? 1 : 0;
  if (value < origin - EPSILON && offset) return 0;
  const index = Math.floor((value - origin) / width + EPSILON) + offset;
  return Math.max(0, Math.min(index, bins.length - 1));
}

// Distribution of ascending values on the shared bins
export function distribution(sorted: number[], layout: BinLayout, levels: HistogramLevel[]): Distribution {
  const counts = layout.bins.map(() => 0);
  sorted.forEach((value) => {
    counts[binIndex(value, layout)] += 1;
  });
  const total = sorted.length;
  const share = (count: number) => (total ? round((count / total) * 100, 2) : 0);

  let levelShares: LevelShare[] = [];
  if (levels.length > 0) {
    const severityCounts = new Map<Severity, number>();
    sorted.forEach((value) => {
      const severity = valueSeverity(value, levels);
      severityCounts.set(severity, (severityCounts.get(severity) ?? 0) + 1);
    });
    levelShares = alertSeverities(levels).map((severity) => ({ severity, percent: share(severityCounts.get(severity) ?? 0) }));
  }

  return { readings: total, percents: counts.map(share), ...valueStats(sorted), levelShares };
}

// Expected minutes between readings: the aggregation interval, else the median gap between
// the readings; null when it cannot be told
export function readingIntervalMinutes(timestamps: number[], aggregate?: number | null): number | null {
  if (aggregate) return aggregate;
  const unique = Array.from(new Set(timestamps)).sort((a, b) => a - b);
  const gaps = unique.slice(1).map((ts, index) => ts - unique[index]);
  if (gaps.length === 0) return null;
  gaps.sort((a, b) => a - b);
  const middle = Math.floor(gaps.length / 2);
  const median = gaps.length % 2 ? gaps[middle] : (gaps[middle - 1] + gaps[middle]) / 2;
  return median / MINUTE_MS;
}

// Readings expected every intervalMinutes from `from` (inclusive) to `to` (exclusive)
export function expectedReadings(from: number, to: number, intervalMinutes: number): number {
  const intervalMs = intervalMinutes * MINUTE_MS;
  if (to <= from || intervalMs <= 0) return 0;
  return Math.max(0, Math.ceil((to - from) / intervalMs - EPSILON));
}

// Readings received vs expected over the period; null when the reading interval is unknown
export function coverage(timestamps: number[], { from, to, aggregate }: { from: number; to: number; aggregate?: number | null }): Coverage | null {
  const interval = readingIntervalMinutes(timestamps, aggregate);
  if (!interval) return null;
  const expected = expectedReadings(from, to, interval);
  if (!expected) return null;
  const received = Math.min(new Set(timestamps.filter((ts) => ts >= from && ts < to)).size, expected);
  return { percent: round((received / expected) * 100, 1), missingMinutes: Math.round((expected - received) * interval) };
}

// Shared y axis maximum: the next multiple of 5 percent (at least 5), capped at 100
export function niceYMax(value: number): number {
  if (value <= 0) return 5;
  return Math.min(100, Math.ceil(value / 5 - EPSILON) * 5);
}

// Histograms of several devices' readings of one parameter on shared bins and y axis
export function buildHistogram(
  devices: DeviceReadings[],
  levels: HistogramLevel[],
  period: { from: number; to: number; aggregate?: number | null }
): HistogramSummary {
  const allValues = devices.flatMap(({ readings }) => readings.map(([, value]) => value));
  const layout = buildBins(allValues, levels);
  const distributions = devices.map(({ id, readings }) => {
    const deviceCoverage = coverage(
      readings.map(([ts]) => ts),
      period
    );
    const values = readings.map(([, value]) => value).sort((a, b) => a - b);
    return {
      id,
      ...distribution(values, layout, levels),
      coverage: deviceCoverage?.percent ?? null,
      missingMinutes: deviceCoverage?.missingMinutes ?? null
    };
  });
  const maxPercent = Math.max(0, ...distributions.flatMap(({ percents }) => percents));
  return {
    hasLevels: levels.length > 0,
    width: layout.width,
    widthLabel: formatNumber(layout.width, layout.width),
    bins: layout.bins,
    edgeLabels: edgeLabels(layout.bins, layout.width),
    levelLines: layout.levelLines,
    yMax: niceYMax(maxPercent),
    devices: distributions
  };
}
